import { PlayCircleOutline as PlayCircleOutlineIcon } from '@mui/icons-material';
import { IconButton, Stack, TableCell } from '@mui/material';
import { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Pagination } from 'src/components/pagination/Pagination';
import { Progress } from 'src/components/progress';
import { CustomTable } from 'src/components/table';
import { TableControlsHeader } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader';
import { ControlType } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { lang } from 'src/utils';

import { StepContentWrapper } from '../../ScheduleComponentPage.styled';
import {
  COLLAPSE_TABLE_BODY_STYLES,
  COLLAPSE_TABLE_TITLE_STYLES,
  EXPORT_HEADERS,
  MAIN_TABLE_TITLE_STYLES,
  MULTIPLE_LOCATION_GROUPS_TABLE_COLUMNS,
  SINGLE_LOCATION_GROUP_TABLE_COLUMNS,
} from './constants';
import { generateSidePanelData } from './helper';
import { SidePanel } from './modules/sidePanel/SidePanel';
import {
  getMultipleLocationGroupTableRows,
  getSingleLocationGroupTableRows,
} from './ReviewPageContent.helper';
import { useReviewPageContent } from './ReviewPageContent.hook';
import {
  StyledExpandMoreIcon,
  StyledPreviewButton,
} from './ReviewPageContent.styles';
import { ReviewPageContentHeader } from './ReviewPageContentHeader';

export interface ReviewPageContentProps {
  componentNames: string[];
  positionString: string;
  earliestScheduleStartDate?: string;
}

export const ReviewPageContent = (props: ReviewPageContentProps) => {
  const [csvData, setCsvData] = useState<any[]>([]);
  const csvLinkRef = useRef<any>(null);

  const {
    totalCount,
    contexts,
    rowsPerPage,
    currentPage,
    totalPages,
    handlePageChange,
    handleRowsPerPageChange,
    selectedSortOption,
    onSortChangeHandle,
    locationGroups,
    mainExpandedRowIndex,
    isLocationGroupsLoading,
    isLocationsLoading,
    handleMainRowExpand,
    generateExportData,
    locationGroupDefaultAssemblies,
    totalLocationGroups,
  } = useReviewPageContent(props.earliestScheduleStartDate);

  const handleExportClick = () => {
    const data = generateExportData(props.componentNames, props.positionString);
    if (data.length === 0) return;

    setCsvData(data);
    setTimeout(() => {
      csvLinkRef.current?.link?.click();
    }, 0);
  };

  const isSingleLocationGroup = totalLocationGroups === 1;

  const tableColumns = isSingleLocationGroup
    ? SINGLE_LOCATION_GROUP_TABLE_COLUMNS
    : MULTIPLE_LOCATION_GROUPS_TABLE_COLUMNS;

  const singleLocationGroupRows = isSingleLocationGroup
    ? getSingleLocationGroupTableRows(
        locationGroups,
        contexts,
        props.positionString,
        locationGroupDefaultAssemblies,
      )
    : null;

  const multipleLocationGroupRows = !isSingleLocationGroup
    ? getMultipleLocationGroupTableRows(
        locationGroups,
        contexts,
        props.positionString,
        locationGroupDefaultAssemblies,
      )
    : null;

  // Location groups with no NSNs must not be expandable (a whole-row click would
  // otherwise still toggle them). Collapsing (index === -1) is always allowed.
  const handleExpandableRowExpand = (index: number) => {
    if (index !== -1 && !multipleLocationGroupRows?.[index]?.hasNsns) {
      return;
    }

    handleMainRowExpand(index);
  };

  return (
    <StepContentWrapper
      data-testid="container"
      sx={{
        display: 'flex',
        flex: 1,
        padding: '40px 32px',
        height: 'unset',
      }}>
      {isLocationGroupsLoading ? (
        <Progress />
      ) : (
        <Stack gap={2}>
          <ReviewPageContentHeader
            hasData={
              (singleLocationGroupRows ?? multipleLocationGroupRows ?? [])
                .length > 0
            }
            onExportClick={handleExportClick}
            isPreviewEnabled={Boolean(
              contexts.isPreviewEnabled && contexts.handleCreatePreviewSchedule,
            )}
            isSingleLocationGroup={isSingleLocationGroup}
            onPreviewClick={() => contexts.handleCreatePreviewSchedule?.()}
          />
          <CSVLink
            data={csvData}
            headers={EXPORT_HEADERS}
            filename={`schedule-components-review-changes-${
              new Date().toISOString().split('T')[0]
            }.csv`}
            ref={csvLinkRef}
            style={{ display: 'none' }}
            enclosingCharacter=""
            separator=","
          />

          <TableControlsHeader
            page={currentPage}
            totalCount={totalCount}
            onSortChange={onSortChangeHandle}
            selectedSortOption={selectedSortOption}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            visibleControls={[ControlType.ITEMS_PER_PAGE, ControlType.SORT]}
          />

          <Stack
            direction={{ sm: 'column-reverse', md: 'row' }}
            gap={2}
            flex={1}>
            <SidePanel
              containerSx={{ flex: 0.4 }}
              title={`${contexts.scheduleName} details`}
              items={generateSidePanelData(contexts.screensGridSections)}
            />

            <Stack flex={1}>
              {isSingleLocationGroup && singleLocationGroupRows ? (
                <CustomTable
                  columns={tableColumns}
                  rows={singleLocationGroupRows}
                  tableCellHeadProps={{
                    sx: MAIN_TABLE_TITLE_STYLES,
                  }}
                />
              ) : multipleLocationGroupRows ? (
                <CustomTable
                  columns={tableColumns}
                  rows={multipleLocationGroupRows}
                  customCellRenderer={{
                    preview: (row) => (
                      <TableCell align="right">
                        <StyledPreviewButton
                          variant="text"
                          size="small"
                          startIcon={<PlayCircleOutlineIcon fontSize="small" />}
                          disabled={!contexts.isPreviewEnabled}
                          onClick={(e: Event) => {
                            e.stopPropagation();
                            contexts.handleCreatePreviewSchedule?.(row.id);
                          }}>
                          {lang.preview}
                        </StyledPreviewButton>
                      </TableCell>
                    ),
                  }}
                  renderActions={(row) => {
                    if (!row.hasNsns) {
                      return null;
                    }

                    const rowIndex = multipleLocationGroupRows.findIndex(
                      (r) => r.id === row.id,
                    );

                    return (
                      <IconButton
                        aria-label={
                          mainExpandedRowIndex === rowIndex
                            ? 'expanded'
                            : 'collapsed'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMainRowExpand(rowIndex);
                        }}
                        sx={{ padding: 0 }}>
                        <StyledExpandMoreIcon
                          data-testid="expand-icon"
                          isExpanded={mainExpandedRowIndex === rowIndex}
                        />
                      </IconButton>
                    );
                  }}
                  renderCollapseContent={(row) => {
                    if (!row.hasNsns) {
                      return null;
                    }

                    return (
                      <CustomTable
                        loading={isLocationsLoading}
                        noBorder
                        columns={row.columns}
                        rows={row?.rows ?? []}
                        tableCellHeadProps={{
                          sx: COLLAPSE_TABLE_TITLE_STYLES,
                        }}
                        tableBodyRowProps={{
                          sx: {
                            ...COLLAPSE_TABLE_BODY_STYLES,
                            ...(props.componentNames.length > 1 && {
                              verticalAlign: 'top',
                            }),
                          },
                        }}
                      />
                    );
                  }}
                  expandedRowIndex={mainExpandedRowIndex}
                  onRowExpand={handleExpandableRowExpand}
                  tableCellHeadProps={{
                    sx: MAIN_TABLE_TITLE_STYLES,
                  }}
                />
              ) : null}
            </Stack>
          </Stack>

          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        </Stack>
      )}
    </StepContentWrapper>
  );
};
